import { Avatar, Container, Link, Stack } from '@mui/material';
import { getBurgerMenuItems } from 'API/Home/burgerMenuItems';
import { BurgerMenu } from 'Components/BurgerMenu';
import { Feedback } from 'Components/Feedback';
import { Footer } from 'Components/Footer';
import { Header2 } from 'Components/Header2';
import { PublicHeader } from 'Components/PublicHeader';
import { UserInfo } from 'Components/UserInfo';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useAuth, useDispatchApp, useIsDesktop } from 'Hooks';
import { useFocusFirstPossibleElement } from 'Hooks/useFocusFirstPossibleElement';
import { useShowUserInfo } from 'Hooks/useShowUserInfo';
import { SuppliersBGDesk, SuppliersBGMobile } from 'Images';
import { setMenuItems } from 'Store/MenuItemsSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

export interface ISuppliersOutletContext {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

export const SuppliersLayout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isDesktop = useIsDesktop();
  const { isAuth, isOtp } = useAuth();
  const showUserInfo = useShowUserInfo();

  const { authenticatedUser, fakeUser } = useAppSelector(
    ({ imposter }) => imposter,
  );

  const mainContent = useRef<HTMLDivElement | null>(null);

  const isUser = fakeUser?.israeliId || authenticatedUser?.israeliId;

  /** Avoid mounting UserInfo for guests so getUser() does not 401-trigger login on /suppliers. */
  const shouldMountUserInfo = showUserInfo && (isAuth || isOtp || !!isUser);

  const burgerMenuItems = useAppSelector(
    ({ menuItems: { menuItems } }) => menuItems,
  );

  const dispatch = useDispatchApp();

  const initMenuItems = useCallback(async () => {
    if (burgerMenuItems.length === 0 && isUser) {
      const menuItemsResult = await getBurgerMenuItems();
      dispatch(setMenuItems(menuItemsResult));
    }
  }, [burgerMenuItems.length, isUser, dispatch]);

  useEffect(() => {
    initMenuItems();
  }, [initMenuItems]);

  useFocusFirstPossibleElement(mainContent.current);

  const getIsFeedbackVisible = () =>
    !isLoading &&
    window.location.pathname !== '/suppliers' &&
    window.location.pathname !== '/suppliers/';

  return (
    <>
      <Link className='sr-only' href={'#main-content'}>
        {Dictionary[DICTIONARY.General].skipToSiteContent}
      </Link>
      {isUser ? (
        <Header2 />
      ) : (
        <PublicHeader style={{ fontFamily: 'Assistant' }} />
      )}
      <Stack
        sx={{
          bgcolor: isDesktop ? 'primary.shade_50' : undefined,
        }}
      >
        <Avatar
          alt=''
          src={isDesktop ? SuppliersBGDesk : SuppliersBGMobile}
          sx={{
            width: '100%',
            height: isDesktop ? '388px' : '240px',
            position: 'absolute',
            top: '72px',
            right: 0,
            left: 0,
          }}
        />
        <Container component={'main'}>
          <Stack
            sx={{
              minHeight: 'calc(100vh - 72px - 100px)',
            }}
            ref={mainContent}
            direction={'row'}
          >
            {isUser && <BurgerMenu />}
            <Stack>
              <Stack
                id='main-content'
                tabIndex={-1}
                sx={{
                  paddingTop: { sm: '28px', xs: '20px' },
                  paddingBottom: { sm: '80px', xs: '65px' },
                  paddingRight: { lg: '275px', sm: '24px', xs: '0px' },
                  paddingLeft: { sm: '275px', xs: '0px' },
                }}
              >
                {shouldMountUserInfo && <UserInfo />}
                <Outlet context={{ isLoading, setIsLoading }} />
              </Stack>
              {getIsFeedbackVisible() && <Feedback />}
            </Stack>
          </Stack>
        </Container>
      </Stack>
      <Footer />
    </>
  );
};
